import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { UNCLASSIFIED_COMPANY, type CompanyView } from '@persped/shared';
import { useState } from 'react';
import {
  useAvailablePrinters,
  useCompanies,
  useCreateCompany,
  useCreatePrinter,
  useEffectivePrinter,
  useCreateSpineIgnorePattern,
  usePrinters,
  useReparseSpine,
  useSpineIgnorePatterns,
  useUpdateCompany,
  useUpdatePrinter,
  useUpdateSpineIgnorePattern,
} from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

/**
 * Törzsadatok — cégek és a gerincadatok újraelemzése (F1, F2).
 *
 * A cégek 2026-08-14-ig Prisma enumként éltek, ezért egy új cég felvétele
 * séma-változás, build és kitelepítés lett volna. A 2026-08-12-i követelmény
 * viszont kimondta, hogy a listának adminisztrálhatónak kell lennie — a
 * 4000 mappás költözés közben egy tizedik cég felbukkanása különben leállást
 * jelentene.
 *
 * ⚠ NINCS TÖRLÉS, csak deaktiválás. Egy használatban lévő cég törlése
 * történetet csonkítana; az adatbázis idegen kulcsa (RESTRICT) amúgy is
 * tiltja.
 */
export function MasterDataPage() {
  const companies = useCompanies();
  const [createOpen, setCreateOpen] = useState(false);
  const [edit, setEdit] = useState<CompanyView | null>(null);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
          Törzsadatok
        </Typography>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Új cég
        </Button>
      </Stack>

      {companies.isError && <ProblemAlert error={companies.error} />}

      <Paper variant="outlined">
        <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>
          Cégek
        </Typography>
        <Divider />
        {companies.isPending ? (
          <Stack sx={{ py: 4, alignItems: 'center' }}>
            <CircularProgress />
          </Stack>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Név</TableCell>
                  <TableCell>Kód</TableCell>
                  <TableCell>Névváltozatok a gerincen</TableCell>
                  <TableCell align="right">Mappák</TableCell>
                  <TableCell>Állapot</TableCell>
                  <TableCell align="right">Műveletek</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(companies.data ?? []).map((c) => (
                  <TableRow key={c.code} sx={{ opacity: c.isActive ? 1 : 0.55 }}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{c.code}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {c.aliases.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        ) : (
                          c.aliases.map((a) => (
                            <Chip key={a} size="small" variant="outlined" label={a} />
                          ))
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{c.folderCount}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={c.isActive ? 'success' : 'default'}
                        label={c.isActive ? 'Aktív' : 'Inaktív'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => setEdit(c)}>
                        Szerkesztés
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography
          variant="caption"
          sx={{ px: 2, py: 1.5, display: 'block' }}
          color="text.secondary"
        >
          A névváltozatok ékezet- és kisbetű-érzéketlenek: a „Perspéd" és a „PERSPED" is ugyanarra
          fut. Egy változat csak EGY céghez tartozhat. Cég törlése nincs — a már besorolt mappák
          története nem csonkítható; helyette deaktiválás.
        </Typography>
      </Paper>

      <SpineIgnoreCard />

      <ReparseCard />

      <PrinterCard />

      <NewCompanyDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      {edit && <EditCompanyDialog company={edit} onClose={() => setEdit(null)} />}
    </Stack>
  );
}

/**
 * Kihagyandó gerincminták (G3, 2026-08-14-i CR #3).
 *
 * A mappára ragasztott címkén ott a rendszernév, és az Azure AI ezt is
 * beolvassa a gerincfotóról. Enélkül a cégillesztés MINDEN mappát Perspedként
 * ismerne fel.
 *
 * A SZERKESZTÉS szándékosan szűk: a minta szövege nem módosítható, csak
 * ki/be kapcsolható. Egy elgépelt minta javítása új sor felvétele és a régi
 * kikapcsolása — így a naplóból látszik, mikortól meddig melyik szűrés volt
 * érvényben.
 */
function SpineIgnoreCard() {
  const patterns = useSpineIgnorePatterns();
  const create = useCreateSpineIgnorePattern();
  const update = useUpdateSpineIgnorePattern();
  const [pattern, setPattern] = useState('');
  const [note, setNote] = useState('');

  return (
    <Paper variant="outlined">
      <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>
        Kihagyandó minták a cégfelismerésnél
      </Typography>
      <Divider />

      <Stack spacing={2} sx={{ p: 2 }}>
        <Alert severity="info">
          <AlertTitle>Miért van erre szükség</AlertTitle>
          A mappára ragasztott címkén ott a rendszernév („Persped Irattár — Mappa"), és az Azure AI
          ezt is beolvassa a gerincfotóról. Ezek a minták a cégillesztés <strong>előtt</strong>
          {' '}kikerülnek a szövegből — a tárolt gerincszöveghez viszont nem nyúlunk.
        </Alert>

        {(patterns.isError || create.isError || update.isError) && (
          <ProblemAlert error={patterns.error ?? create.error ?? update.error} />
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Minta</TableCell>
                <TableCell>Megjegyzés</TableCell>
                <TableCell>Állapot</TableCell>
                <TableCell align="right">Művelet</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(patterns.data ?? []).map((p) => (
                <TableRow key={p.id} sx={{ opacity: p.isActive ? 1 : 0.55 }}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{p.pattern}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {p.note ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={p.isActive ? 'success' : 'default'}
                      label={p.isActive ? 'Aktív' : 'Kikapcsolva'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({ id: p.id, isActive: !p.isActive, rowVersion: p.rowVersion })
                      }
                    >
                      {p.isActive ? 'Kikapcsolás' : 'Bekapcsolás'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Új minta"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            sx={{ minWidth: 260 }}
            helperText="A szerver ékezet nélkülire és kisbetűsre alakítja."
          />
          <TextField
            size="small"
            label="Megjegyzés"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ minWidth: 260 }}
            helperText="Miért van erre szükség — fél év múlva ez lesz a válasz."
          />
          <Button
            variant="outlined"
            disabled={create.isPending || pattern.trim().length < 3}
            onClick={() =>
              create.mutate(
                { pattern: pattern.trim(), note: note.trim() },
                { onSuccess: () => { setPattern(''); setNote(''); } },
              )
            }
          >
            Felvétel
          </Button>
        </Stack>

        <Alert severity="warning">
          A minta a <strong>teljes feliratra</strong> illeszkedjen, sose egyetlen szóra. A
          „persped" önmagában felvéve a valódi Persped-besorolást is elvinné — és mivel a mappák
          többsége az övé, ez csendben a keresés fő szűrőjét rontaná el.
        </Alert>
      </Stack>
    </Paper>
  );
}

/**
 * Nyomtató (G2, 2026-08-14-i igény; bővítve 2026-08-26).
 *
 * A PrintNode-azonosító eddig környezeti változó volt, tehát egy másik Zebra
 * nyomtatóra váltás kitelepítést jelentett. Fizikailag ez perces művelet.
 *
 * A választás LISTÁBÓL történik, nem szabad szövegből: egy elgépelt
 * azonosító különben csak az első sikertelen nyomtatáskor derülne ki — vagyis
 * akkor, amikor már áll a munka.
 *
 * ═══ ÉLES ÜZEM (2026-08-26) ═══
 *
 * Amióta a rendszer valódi munkát szolgál ki, két dolog vált fontossá:
 *
 *  - **Látszania kell, hova megy MOST a nyomtatás.** Üres törzsadatnál a
 *    szerver a `PRINTNODE_PRINTER_ID` beállítására esik vissza, és ez a szám
 *    sehol nem szerepelt a felületen. Amikor nem jön ki a címke, ez az első
 *    kérdés.
 *  - **A felvétel NEM kapcsolhat át némán.** Korábban minden hozzáadás
 *    azonnal aktívvá is tette az új nyomtatót — vagyis aki csak fel akarta
 *    venni a listára a tartalék gépet, az észrevétlenül átirányította oda az
 *    összes címkét. A két művelet mostantól külön gomb.
 */
function PrinterCard() {
  const printers = usePrinters();
  const effective = useEffectivePrinter();
  const [valasztoNyitva, setValasztoNyitva] = useState(false);
  const [atnevezendo, setAtnevezendo] = useState<{ id: string; nev: string; rowVersion: number } | null>(
    null,
  );
  const available = useAvailablePrinters(valasztoNyitva);
  const create = useCreatePrinter();
  const update = useUpdatePrinter();

  const felvettIdk = new Set((printers.data ?? []).map((p) => p.printnodeId));
  const eff = effective.data;

  return (
    <Paper variant="outlined">
      <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>
        Címkenyomtató
      </Typography>
      <Divider />

      <Stack spacing={2} sx={{ p: 2 }}>
        {(printers.isError || create.isError || update.isError) && (
          <ProblemAlert error={printers.error ?? create.error ?? update.error} />
        )}

        {/* HOVA MEGY MOST A NYOMTATÁS — a lap legfontosabb sora. */}
        {eff && (
          <Alert severity={eff.source === 'NONE' ? 'error' : 'success'}>
            {eff.source === 'NONE' ? (
              <>
                <strong>Nincs beállított nyomtató.</strong> A címkenyomtatás nem fog működni, amíg
                nem veszel fel egyet.
              </>
            ) : (
              <>
                Jelenleg ide nyomtatunk:{' '}
                <strong>
                  {eff.name ?? 'PrintNode-azonosító'} ({eff.printnodeId})
                </strong>
                {eff.source === 'CONFIG' && (
                  <>
                    {' '}
                    — a <strong>szerverbeállításból</strong>. Ez csak kitelepítéssel változtatható.
                    Vegyél fel egy nyomtatót alább, ha innen, a felületről akarsz váltani.
                  </>
                )}
              </>
            )}
          </Alert>
        )}

        {(printers.data ?? []).length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Név</TableCell>
                  <TableCell align="right">PrintNode-azonosító</TableCell>
                  <TableCell>Állapot</TableCell>
                  <TableCell align="right">Művelet</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(printers.data ?? []).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      {atnevezendo?.id === p.id ? (
                        <TextField
                          size="small"
                          autoFocus
                          value={atnevezendo.nev}
                          onChange={(e) =>
                            setAtnevezendo({ ...atnevezendo, nev: e.target.value })
                          }
                          sx={{ minWidth: 220 }}
                        />
                      ) : (
                        p.name
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {p.printnodeId}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={p.isActive ? 'success' : 'default'}
                        label={p.isActive ? 'Erre nyomtatunk' : 'Inaktív'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                        {atnevezendo?.id === p.id ? (
                          <>
                            <Button
                              size="small"
                              disabled={update.isPending || atnevezendo.nev.trim().length === 0}
                              onClick={() =>
                                update.mutate(
                                  {
                                    id: p.id,
                                    name: atnevezendo.nev.trim(),
                                    rowVersion: atnevezendo.rowVersion,
                                  },
                                  { onSuccess: () => setAtnevezendo(null) },
                                )
                              }
                            >
                              Mentés
                            </Button>
                            <Button size="small" onClick={() => setAtnevezendo(null)}>
                              Mégse
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="small"
                              onClick={() =>
                                setAtnevezendo({ id: p.id, nev: p.name, rowVersion: p.rowVersion })
                              }
                            >
                              Átnevezés
                            </Button>
                            {/*
                              A kikapcsolást SZÁNDÉKOSAN nem kínáljuk az aktív
                              nyomtatóra: az utolsó aktív kikapcsolása némán a
                              szerverbeállításra ejtené vissza a nyomtatást, egy
                              másik, esetleg már nem is létező nyomtatóra.
                              Váltani a MÁSIK sor „Ezt használjuk" gombjával
                              lehet — az egy lépésben, ütközés nélkül cserél.
                            */}
                            <Button
                              size="small"
                              disabled={update.isPending || p.isActive}
                              onClick={() =>
                                update.mutate({ id: p.id, isActive: true, rowVersion: p.rowVersion })
                              }
                            >
                              Ezt használjuk
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!valasztoNyitva ? (
          <Button variant="outlined" sx={{ alignSelf: 'flex-start' }} onClick={() => setValasztoNyitva(true)}>
            Nyomtató hozzáadása
          </Button>
        ) : (
          <Stack spacing={1}>
            <Typography variant="subtitle2">A PrintNode-fiókban elérhető nyomtatók</Typography>
            <Typography variant="caption" color="text.secondary">
              A lista abból a PrintNode-fiókból jön, amivel a szerver dolgozik. Ha egy nyomtató
              hiányzik, azon a gépen nem fut a PrintNode kliens, vagy másik fiókba van bejelentkezve.
            </Typography>
            {available.isPending && <CircularProgress size={20} />}
            {available.isError && <ProblemAlert error={available.error} />}
            {(available.data ?? []).map((p) => (
              <Stack
                key={p.id}
                direction="row"
                spacing={2}
                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Typography variant="body2" sx={{ minWidth: 200 }}>
                  {p.name}
                </Typography>
                <Chip size="small" variant="outlined" label={p.state} />
                <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {p.computerName ?? '—'}
                </Typography>
                {felvettIdk.has(p.id) ? (
                  <Typography variant="caption" color="text.secondary">
                    Már felvéve
                  </Typography>
                ) : (
                  <>
                    {/*
                      KÉT KÜLÖN GOMB. A felvétel önmagában nem irányítja át a
                      nyomtatást — aki csak a tartalék gépet akarja felvenni a
                      listára, az ne kapcsolja át észrevétlenül az összes
                      címkét egy másik nyomtatóra.
                    */}
                    <Button
                      size="small"
                      disabled={create.isPending}
                      onClick={() =>
                        create.mutate(
                          { printnodeId: p.id, name: p.name, isActive: false },
                          { onSuccess: () => setValasztoNyitva(false) },
                        )
                      }
                    >
                      Felvétel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={create.isPending}
                      onClick={() =>
                        create.mutate(
                          { printnodeId: p.id, name: p.name, isActive: true },
                          { onSuccess: () => setValasztoNyitva(false) },
                        )
                      }
                    >
                      Felvétel és átállás
                    </Button>
                  </>
                )}
              </Stack>
            ))}
            <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => setValasztoNyitva(false)}>
              Mégse
            </Button>
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary">
          A PrintNode API-kulcs <strong>nem</strong> ezen a felületen él — az a szerver
          titokkezelőjében marad. Innen csak azt választod ki, melyik nyomtatóra menjenek a címkék.

        </Typography>
      </Stack>
    </Paper>
  );
}

/**
 * A gerincadatok újraelemzése (F2).
 *
 * KÉTLÉPCSŐS szándékosan: előbb megmutatja, hányat érintene, és csak utána
 * ír. Egy elgépelt alias („green") különben egy kattintással átsorolhatna
 * több száz mappát, és csak az eseménynaplóból derülne ki.
 */
function ReparseCard() {
  const reparse = useReparseSpine();
  const eredmeny = reparse.data;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">Gerincadatok újraelemzése</Typography>

        <Alert severity="info">
          A cégek és névváltozatok módosítása <strong>nem hat visszamenőleg</strong> a már
          rögzített mappákra. Ez a művelet futtatja végig rajtuk a mai szabályokat — a{' '}
          <strong>kézzel javított mezőket nem bántja</strong>.
        </Alert>

        {reparse.isError && <ProblemAlert error={reparse.error} />}

        {eredmeny && (
          <Alert severity={eredmeny.dryRun ? 'warning' : 'success'}>
            <AlertTitle>
              {eredmeny.dryRun
                ? `${eredmeny.affected} mappa gerincadata változna`
                : `${eredmeny.updated} mappa gerincadata frissült`}
            </AlertTitle>
            <Typography variant="body2">
              Megvizsgálva: {eredmeny.examined} mappa (amelyiknek van gerincszövege).
            </Typography>
            {eredmeny.samples.length > 0 && (
              <Stack sx={{ mt: 1 }}>
                {eredmeny.samples.map((s, i) => (
                  <Typography key={`${s.barcode}-${s.field}-${i}`} variant="caption">
                    {s.barcode} — {s.field}: {s.from ?? '—'} → {s.to ?? '—'}
                  </Typography>
                ))}
              </Stack>
            )}
          </Alert>
        )}

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            disabled={reparse.isPending}
            onClick={() => reparse.mutate({ dryRun: true })}
          >
            Mit érintene?
          </Button>
          <Button
            variant="contained"
            color="warning"
            // Csak a próbafuttatás UTÁN, és csak ha van mit írni — így nem
            // lehet „vakon" végigfuttatni több ezer mappán.
            disabled={reparse.isPending || !eredmeny?.dryRun || eredmeny.affected === 0}
            onClick={() => reparse.mutate({ dryRun: false })}
          >
            Újraelemzés futtatása
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/** Az aliasok vesszővel elválasztva — a felület legegyszerűbb alakja rövid listákhoz. */
function parseAliases(raw: string): string[] {
  return raw
    .split(',')
    .map((a) => a.trim())
    .filter((a) => a.length > 0);
}

function NewCompanyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateCompany();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [aliases, setAliases] = useState('');

  const close = () => {
    create.reset();
    setCode('');
    setName('');
    setAliases('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Új cég</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Név"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Ahogy a felületen megjelenik, pl. „Green Biotic”."
            fullWidth
          />
          <TextField
            label="Kód"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
            helperText="Nagybetűs azonosító, pl. GREEN_BIOTIC. KÉSŐBB NEM MÓDOSÍTHATÓ — a mappák erre hivatkoznak."
            fullWidth
          />
          <TextField
            label="Névváltozatok a gerincen (vesszővel)"
            value={aliases}
            onChange={(e) => setAliases(e.target.value)}
            helperText="pl. green biotic, greenbiotic. Ékezet és kis/nagybetű nem számít."
            fullWidth
          />
          {create.isError && <ProblemAlert error={create.error} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Mégse</Button>
        <Button
          variant="contained"
          disabled={create.isPending || code.length < 2 || name.trim().length === 0}
          onClick={() =>
            create.mutate(
              { code, name: name.trim(), aliases: parseAliases(aliases) },
              { onSuccess: close },
            )
          }
        >
          Létrehozás
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditCompanyDialog({ company, onClose }: { company: CompanyView; onClose: () => void }) {
  const update = useUpdateCompany();
  const [name, setName] = useState(company.name);
  const [aliases, setAliases] = useState(company.aliases.join(', '));

  const isUnclassified = company.code === UNCLASSIFIED_COMPANY;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{company.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isUnclassified && (
            <Alert severity="info">
              A „Besorolatlan” nem deaktiválható: ez az alapértelmezés minden mappára, aminek a
              gerincéről nem azonosítható a cég.
            </Alert>
          )}
          {company.folderCount > 0 && !isUnclassified && (
            <Alert severity="warning">
              Ehhez a céghez <strong>{company.folderCount} mappa</strong> tartozik. A deaktiválás
              nem sorolja át őket — csak új besorolásra nem lesz felkínálva.
            </Alert>
          )}

          <TextField
            label="Név"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Névváltozatok a gerincen (vesszővel)"
            value={aliases}
            onChange={(e) => setAliases(e.target.value)}
            helperText="Teljes csere: ami itt nem szerepel, az törlődik."
            fullWidth
          />
          {update.isError && <ProblemAlert error={update.error} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        {!isUnclassified && (
          <Button
            color={company.isActive ? 'warning' : 'success'}
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                {
                  code: company.code,
                  isActive: !company.isActive,
                  rowVersion: company.rowVersion,
                },
                { onSuccess: onClose },
              )
            }
          >
            {company.isActive ? 'Deaktiválás' : 'Aktiválás'}
          </Button>
        )}
        <Button onClick={onClose}>Mégse</Button>
        <Button
          variant="contained"
          disabled={update.isPending || name.trim().length === 0}
          onClick={() =>
            update.mutate(
              {
                code: company.code,
                name: name.trim(),
                aliases: parseAliases(aliases),
                rowVersion: company.rowVersion,
              },
              { onSuccess: onClose },
            )
          }
        >
          Mentés
        </Button>
      </DialogActions>
    </Dialog>
  );
}
